import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Modal,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { countryList, getRegionCode } from "../../../utilities/countries";
import { validateAddressWithAPI } from '../../../helpers/addressValidation';
import {
  addCustomer,
  updateCustomer,
  clearCustomerMessages,
  getCustomerOrgAccounts,
  resetCustomerOrgAccounts,
} from '../../../store/customer/customerSlice';
import { getAccountCloudType } from "../../../CloudProviderContext";
import { useAccounts } from "../../../components/Common/HelperComponents/GetDataFromStore";
import { AWS_ACCOUNT_NUMBER } from "../../Compliance/AuditHub/constants";
import { AWS_REGION_CODES } from "../../Compliance/AuditHub/utils";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
  primaryButtonSx,
} from "../../Compliance/AuditHub/theme";
import {
  autocompleteSlotProps,
  fieldSx,
} from "../../PartnerCentral/formFieldStyles";
import {
  buildCommercialFields,
  buildJavaCustomerUpdatePayload,
  formFromOrganizationCustomer,
  selectedCustomerAccountIds,
  validateActiveCustomer,
} from "../../PartnerCentral/customerPayload";
import { getOrganizationCustomer } from "../../../helpers/partnerCentral_helper";
import PayerPayeeAccountsField from "../../PartnerCentral/PayerPayeeAccountsField";
import {
  getCustomerAccountNumbers,
  getCustomerDisplayName,
  isCustomerActive,
} from "../utils/customerHelpers";
import FinOpsCustomerCommercialSection, {
  emptyCommercialForm,
} from "./FinOpsCustomerCommercialSection";

const CLOSE_BG = "#74788d";
const CLOSE_TEXT = "#eff2f7";

const asOrganizationCustomerRecord = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (payload.organization_id != null || payload.orgcode || payload.partnerCentral) {
    return payload;
  }
  if (Array.isArray(payload.items) && payload.items[0]) {
    return asOrganizationCustomerRecord(payload.items[0]);
  }
  if (payload.data && typeof payload.data === "object") {
    return asOrganizationCustomerRecord(payload.data);
  }
  return null;
};

const resolveOrganizationId = (customer) => {
  const raw =
    customer?.organization_id ?? customer?.organizationId ?? customer?.orgId;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
};

const dialogButtonSx = {
  minWidth: 140,
  height: 40,
  px: 2,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

/** Cancel | message | action on one row; phones stack them. Message slot always reserved. */
const modalFooterSx = {
  display: "grid",
  flexShrink: 0,
  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "auto minmax(0, 1fr) auto" },
  gridTemplateAreas: { xs: '"message" "actions" "cancel"', sm: '"cancel message actions"' },
  alignItems: "center",
  columnGap: 1.5,
  rowGap: 1.25,
  px: 3,
  py: 2,
  borderTop: `1px solid ${AH_BORDER}`,
  backgroundColor: AH_TRACK,
};

/** Capped at two lines; full text available via title. */
const footerMessageSx = {
  gridArea: "message",
  minWidth: 0,
  fontSize: "0.8125rem",
  lineHeight: 1.45,
  textAlign: "center",
  color: "#d32f2f",
  overflowWrap: "anywhere",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  "&:empty": { display: { xs: "none", sm: "-webkit-box" } },
};

const firstErrorMessage = (errors) => {
  if (!errors) return "";
  if (typeof errors === "string") return errors.trim();
  if (typeof errors !== "object") return "";
  for (const value of Object.values(errors)) {
    const msg = firstErrorMessage(value);
    if (msg) return msg;
  }
  return "";
};

const parseCommercialNumber = (value) => {
  const cleaned = String(value || "").replace(/[$,%/]/g, "").trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const labelSx = {
  display: "block",
  mb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: AH_TEXT,
  lineHeight: 1.4,
};

const hintSx = { mt: 0.5, fontSize: 12, color: AH_MUTED, lineHeight: 1.4 };

const sectionSx = {
  fontSize: 14,
  fontWeight: 700,
  color: AH_TEXT,
  letterSpacing: "-0.01em",
};

const regionChipSx = (selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
  color: AH_TEXT,
  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
});

const regionsSummary = (regions) => {
  if (!regions?.length) return "All regions";
  return regions.length === 1 ? regions[0] : `${regions.length} regions`;
};

const FieldLabel = ({ htmlFor, children, required }) => (
  <Typography component="label" htmlFor={htmlFor} sx={labelSx}>
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }} aria-hidden="true">
        *
      </Box>
    ) : null}
  </Typography>
);

FieldLabel.propTypes = {
  htmlFor: PropTypes.string,
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
};

const CustomerModal = ({ open, onClose, onSuccess, customer = null }) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(customer?.orgcode);
  const {
    isAddingCustomer,
    addCustomerSuccess,
    addCustomerError,
    isUpdatingCustomer,
    updateCustomerSuccess,
    updateCustomerError,
    customerOrgAccountsSuccess,
  } = useSelector((state) => state.customer);
  const orgAccounts = useAccounts();
  const isSaving = isEdit ? isUpdatingCustomer : isAddingCustomer;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [footerError, setFooterError] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);

  // Get mspOrgCode from localStorage
  const getMspOrgCode = () => {
    try {
      const selectedOrganization = localStorage.getItem('selectedOrganization');
      if (selectedOrganization) {
        const orgData = JSON.parse(selectedOrganization);
        return orgData?.orgCode || '';
      }
    } catch (error) {
      console.error('Error getting mspOrgCode from localStorage:', error);
    }
    return '';
  };

  const mspOrgCode = getMspOrgCode();

  // Form State - Organization
  const [orgName, setOrgName] = useState("");
  const [orgDescription, setOrgDescription] = useState("");
  const [orgPrimaryName, setOrgPrimaryName] = useState("");
  const [orgPrimaryEmail, setOrgPrimaryEmail] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [orgCity, setOrgCity] = useState("");
  const [orgProvince, setOrgProvince] = useState("");
  const [orgZipcode, setOrgZipcode] = useState("");
  const [orgCountry, setOrgCountry] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phonenumberError, setPhonenumberError] = useState("");

  const [addressValidationStatus, setAddressValidationStatus] = useState(null);
  const [addressValidationMessage, setAddressValidationMessage] = useState('');

  // AWS payer / payee accounts (same component as Partner Central)
  const [payerAccountNumbers, setPayerAccountNumbers] = useState([]);
  const [payeeAccountNumbers, setPayeeAccountNumbers] = useState([]);
  /** Explicit "Payer:" chips from the Payee dropdown (Selected Accounts). */
  const [payeeUiPayerNumbers, setPayeeUiPayerNumbers] = useState([]);
  const [payersHydrated, setPayersHydrated] = useState(false);
  const [accountsError, setAccountsError] = useState("");

  // Partner Central commercial terms (Java POST partnerCentral)
  const [commercialForm, setCommercialForm] = useState(emptyCommercialForm);
  const [commercialErrors, setCommercialErrors] = useState({});
  const [pendingSaveStatus, setPendingSaveStatus] = useState("saved");

  const payerAccounts = useMemo(() => {
    if (!Array.isArray(orgAccounts)) return [];
    return orgAccounts
      .filter((account) => {
        const number = String(account?.accountNumber ?? "").trim();
        if (!AWS_ACCOUNT_NUMBER.test(number)) return false;
        return getAccountCloudType(account) !== "AZURE";
      })
      .map((account) => ({
        accountNumber: String(account.accountNumber).trim(),
        accountName: account.accountName || "",
      }));
  }, [orgAccounts]);

  // Payload / validation: Selected Accounts only (Payee dropdown), not Payer scope.
  const selectedAccountNumbers = useMemo(
    () =>
      selectedCustomerAccountIds({
        payeeAccountNumbers,
        payeeUiPayerNumbers,
      }),
    [payeeAccountNumbers, payeeUiPayerNumbers]
  );

  // Clear form state
  const clearFormState = () => {
    setOrgName("");
    setOrgDescription("");
    setOrgPrimaryName("");
    setOrgPrimaryEmail("");
    setOrgAddress("");
    setOrgCity("");
    setOrgProvince("");
    setOrgZipcode("");
    setOrgCountry("");
    setPhoneNumber("");
    setPhonenumberError("");
    setAddressValidationStatus(null);
    setAddressValidationMessage('');
    setPayerAccountNumbers([]);
    setPayeeAccountNumbers([]);
    setPayeeUiPayerNumbers([]);
    setPayersHydrated(false);
    setAccountsError("");
    setFooterError("");
    setConfirmDialogOpen(false);
    setCommercialForm(emptyCommercialForm());
    setCommercialErrors({});
    setPendingSaveStatus("saved");
    setLoadingRecord(false);
  };

  const applyOrganizationCustomerRecord = (record) => {
    if (!record) return;
    const mapped = formFromOrganizationCustomer(record);

    if (mapped.organizationName) setOrgName(mapped.organizationName);
    if (mapped.orgDescription) setOrgDescription(mapped.orgDescription);
    if (mapped.primaryContactName) setOrgPrimaryName(mapped.primaryContactName);
    if (mapped.primaryContactEmail) setOrgPrimaryEmail(mapped.primaryContactEmail);
    if (mapped.phoneNumber) setPhoneNumber(mapped.phoneNumber);
    if (mapped.orgAddress) setOrgAddress(mapped.orgAddress);
    if (mapped.orgCity) setOrgCity(mapped.orgCity);
    if (mapped.orgProvince || mapped.orgState) {
      setOrgProvince(mapped.orgProvince || mapped.orgState);
    }
    if (mapped.orgCountry) setOrgCountry(mapped.orgCountry);
    if (mapped.orgZipcode || mapped.orgZip) {
      setOrgZipcode(mapped.orgZipcode || mapped.orgZip);
    }

    if (mapped.payeeAccountNumbers?.length) {
      setPayeeAccountNumbers(mapped.payeeAccountNumbers);
      setPayeeUiPayerNumbers(
        Array.isArray(mapped.payeeUiPayerNumbers) ? mapped.payeeUiPayerNumbers : []
      );
      setPayersHydrated(false);
    }
    if (mapped.payerAccountNumbers?.length) {
      setPayerAccountNumbers(mapped.payerAccountNumbers);
      setPayersHydrated(true);
    }

    setCommercialForm({
      ...emptyCommercialForm(),
      customerId: mapped.customerId || "",
      regions: mapped.regions || [],
      wholesalePrograms: mapped.wholesalePrograms || [],
      startDate: mapped.startDate || "",
      endDate: mapped.endDate || "",
      paymentTerms: mapped.paymentTerms || "",
      multiYearCommitment: mapped.multiYearCommitment || "",
      commitmentDiscount: mapped.commitmentDiscount || "",
      pricingModel: mapped.pricingModel || "",
      serviceExclusions: mapped.serviceExclusions || [],
      commitmentMode: mapped.commitmentMode || "annual",
      years: mapped.commitmentMode === "tiers" ? [] : mapped.years || [],
      tiers: mapped.commitmentMode === "tiers" ? mapped.tiers || [] : [],
    });
  };

  const validateCommercial = (draft) => {
    const next = {};
    if (!draft && !String(commercialForm.customerId || "").trim()) {
      next.customerId = "Enter a customer ID.";
    }
    if (
      commercialForm.startDate &&
      commercialForm.endDate &&
      commercialForm.endDate < commercialForm.startDate
    ) {
      next.endDate = "End date must be on or after the start date.";
    }
    if (String(commercialForm.multiYearCommitment || "").trim()) {
      const amount = parseCommercialNumber(commercialForm.multiYearCommitment);
      if (Number.isNaN(amount) || amount < 0) {
        next.multiYearCommitment = "Enter a commitment amount.";
      }
    }
    if (String(commercialForm.commitmentDiscount || "").trim()) {
      const discount = parseCommercialNumber(commercialForm.commitmentDiscount);
      if (Number.isNaN(discount) || discount < 0 || discount > 100) {
        next.commitmentDiscount = "Enter 0 to 100.";
      }
    }

    const commitmentMode =
      commercialForm.commitmentMode === "tiers" ? "tiers" : "annual";
    if (commitmentMode === "annual") {
      const yearErrors = {};
      (commercialForm.years || []).forEach((year) => {
        const blank =
          !year.label?.trim() &&
          !year.startDate &&
          !year.endDate &&
          !year.commitment?.trim() &&
          !year.discount?.trim();
        if (blank) return;
        const row = {};
        if (!year.label?.trim()) row.label = "Enter a program year identifier.";
        if (year.startDate && year.endDate && year.endDate < year.startDate) {
          row.endDate = "End date is before the start date.";
        }
        const commitment = parseCommercialNumber(year.commitment);
        if (year.commitment?.trim() && (Number.isNaN(commitment) || commitment < 0)) {
          row.commitment = "Enter an amount.";
        }
        const discount = parseCommercialNumber(year.discount);
        if (
          year.discount?.trim() &&
          (Number.isNaN(discount) || discount < 0 || discount > 100)
        ) {
          row.discount = "Enter 0 to 100.";
        }
        if (Object.keys(row).length) yearErrors[year.id] = row;
      });
      if (Object.keys(yearErrors).length) next.years = yearErrors;
    } else {
      const tierErrors = {};
      (commercialForm.tiers || []).forEach((tier) => {
        const blank =
          !tier.name?.trim() && !tier.threshold?.trim() && !tier.discount?.trim();
        if (blank) return;
        const row = {};
        if (!tier.name?.trim()) row.name = "Enter a tier name.";
        const threshold = parseCommercialNumber(tier.threshold);
        if (tier.threshold?.trim() && (Number.isNaN(threshold) || threshold < 0)) {
          row.threshold = "Enter an amount.";
        }
        const discount = parseCommercialNumber(tier.discount);
        if (
          tier.discount?.trim() &&
          (Number.isNaN(discount) || discount < 0 || discount > 100)
        ) {
          row.discount = "Enter 0 to 100.";
        }
        if (Object.keys(row).length) tierErrors[tier.id] = row;
      });
      if (Object.keys(tierErrors).length) next.tiers = tierErrors;
    }

    if (!draft) {
      const activeErrors = validateActiveCustomer({
        ...commercialForm,
        organizationName: orgName,
        primaryContactName: orgPrimaryName,
        primaryContactEmail: orgPrimaryEmail,
        payeeAccountNumbers,
        payeeUiPayerNumbers,
      });
      // Org/account fields are already covered by FinOps identity validation.
      delete activeErrors.organizationName;
      delete activeErrors.primaryContactName;
      delete activeErrors.primaryContactEmail;
      delete activeErrors.payerAccountNumbers;
      Object.assign(next, activeErrors);
    }

    return next;
  };

  // Org / identity fields — always required for Java onboarding
  const isOrgFormInvalid = () => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return (
      !orgName ||
      !orgDescription ||
      !orgPrimaryName ||
      !orgPrimaryEmail ||
      !emailRegex.test(orgPrimaryEmail) ||
      !orgAddress ||
      !orgCountry ||
      addressValidationStatus === "invalid" ||
      selectedAccountNumbers.length === 0 ||
      (phoneNumber && !isPossiblePhoneNumber(phoneNumber))
    );
  };

  // Handle form submission - show confirmation dialog first
  const handleFormSubmit = (e, status = "saved") => {
    e.preventDefault();
    setFooterError("");
    dispatch(clearCustomerMessages());
    if (!selectedAccountNumbers.length) {
      const message = "Select at least one AWS account for this customer.";
      setAccountsError(message);
      setFooterError(message);
      document
        .getElementById("finops-payerAccountNumbers")
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    setAccountsError("");
    if (isOrgFormInvalid()) {
      setFooterError(
        phonenumberError ||
          (addressValidationStatus === "invalid"
            ? addressValidationMessage || "Enter a valid organization address."
            : "Please complete all required organization fields.")
      );
      return;
    }
    const commercialNext = validateCommercial(status === "draft");
    setCommercialErrors(commercialNext);
    if (Object.keys(commercialNext).length) {
      setFooterError(
        firstErrorMessage(commercialNext) || "Please fix the highlighted fields."
      );
      document
        .getElementById("pc-customerId")
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    setPendingSaveStatus(status);
    setConfirmDialogOpen(true);
  };

  // Actually submit the form after confirmation
  const handleConfirmSubmit = () => {
    if (isEdit) {
      dispatch(
        updateCustomer(
          buildJavaCustomerUpdatePayload(
            {
              customerOrgcode: customer.orgcode,
              organizationName: orgName,
              orgDescription,
              primaryContactName: orgPrimaryName,
              primaryContactEmail: orgPrimaryEmail,
              phoneNumber,
              orgAddress,
              orgCity,
              orgProvince,
              orgCountry,
              orgZipcode,
              // Selected Accounts only (Payee dropdown) — not Payer-scope dropdown.
              payeeAccountNumbers,
              payeeUiPayerNumbers,
              ...commercialForm,
            },
            {
              active: isCustomerActive(customer),
              status: pendingSaveStatus,
            }
          )
        )
      );
      setConfirmDialogOpen(false);
      return;
    }

    const newCustomer = {
      mspOrgCode,
      orgname: orgName,
      orgmetadata: "",
      orgdescription: orgDescription,
      orgprimaryname: orgPrimaryName,
      orgprimaryemail: orgPrimaryEmail,
      orgaddress: orgAddress,
      orgcity: orgCity,
      orgstate: orgProvince,
      orgcountry: orgCountry,
      orgzip: orgZipcode,
      orgbillingname: orgPrimaryName,
      orgbillingemail: orgPrimaryEmail,
      orgbillingaddress: orgAddress,
      orgbillingcity: orgCity,
      orgbillingstate: orgProvince,
      orgbillingcountry: orgCountry,
      orgbillingzip: orgZipcode,
      phoneNumber,
      accountNumbers: selectedAccountNumbers,
      partnerCentral: buildCommercialFields(commercialForm, pendingSaveStatus),
    };
    dispatch(addCustomer(newCustomer));
    setConfirmDialogOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("info");
    dispatch(clearCustomerMessages());
  };

  // Handle phone number validation
  const handlePhoneBlur = () => {
    if (!phoneNumber) {
      setPhonenumberError("Phone number is required.");
    } else if (!isPossiblePhoneNumber(phoneNumber)) {
      setPhonenumberError("Please enter a valid phone number with country code (e.g., +1 555 123 4567).");
    } else {
      setPhonenumberError("");
    }
  };


  const validateAddress = async () => {
    if (!orgAddress.trim() || !orgCountry) return;

    setAddressValidationStatus(null);
    setAddressValidationMessage('Validating address...');

    const regionCode = getRegionCode(orgCountry);
    if (!regionCode) {
      setAddressValidationStatus('invalid');
      setAddressValidationMessage('Please select a valid country first');
      return;
    }

    try {
      const result = await validateAddressWithAPI(
        orgAddress, 
        regionCode, 
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY
      );

      setAddressValidationStatus(result.isValid ? 'valid' : 'invalid');
      setAddressValidationMessage(result.message);
      
      setOrgAddress(result.formattedAddress || orgAddress);
      setOrgCity(result.city);
      setOrgProvince(result.state);
      setOrgZipcode(result.zip);
    } catch (error) {
      setAddressValidationStatus('invalid');
      setAddressValidationMessage('Failed to validate address. Please try again.');
    }
  };

  // Reset / hydrate when modal opens; load org accounts for payer/payee field
  useEffect(() => {
    if (!open) return undefined;

    clearFormState();
    dispatch(clearCustomerMessages());
    dispatch(resetCustomerOrgAccounts());
    dispatch(getCustomerOrgAccounts());

    let cancelled = false;
    const organizationId = resolveOrganizationId(customer);

    if (customer?.orgcode || organizationId != null) {
      // Baseline from margin-summary while Java detail loads.
      const baselineAccounts = getCustomerAccountNumbers(customer);
      setOrgName(customer.customer_name || "");
      setOrgDescription(customer.orgdescription || customer.orgDescription || "");
      setOrgPrimaryName(customer.orgprimaryname || customer.orgPrimaryName || "");
      setOrgPrimaryEmail(customer.orgprimaryemail || customer.orgPrimaryEmail || "");
      setOrgAddress(customer.orgaddress || customer.orgAddress || "");
      setOrgCity(customer.orgcity || customer.orgCity || "");
      setOrgProvince(customer.orgstate || customer.orgState || "");
      setOrgZipcode(customer.orgzip || customer.orgZip || "");
      setOrgCountry(customer.orgcountry || customer.orgCountry || "");
      setPhoneNumber(customer.phonenumber || customer.phoneNumber || "");
      setPayeeAccountNumbers(baselineAccounts);
      setPayeeUiPayerNumbers([]);
      setPayersHydrated(false);
    }

    if (organizationId != null) {
      setLoadingRecord(true);
      getOrganizationCustomer(organizationId)
        .then((payload) => {
          if (cancelled) return;
          const record = asOrganizationCustomerRecord(payload);
          if (!record) return;
          applyOrganizationCustomerRecord(record);
        })
        .catch(() => {
          // Detail may be unavailable; margin-summary baseline remains editable.
        })
        .finally(() => {
          if (!cancelled) setLoadingRecord(false);
        });
    }

    return () => {
      cancelled = true;
      dispatch(clearCustomerMessages());
      dispatch(resetCustomerOrgAccounts());
    };
  }, [open, customer, dispatch]);

  // Restore payer chips for edit from org-account payer IDs of saved payees (same as Partner Central).
  useEffect(() => {
    if (!open || !isEdit || payersHydrated || loadingRecord) return;
    if (!payeeAccountNumbers?.length) {
      setPayersHydrated(true);
      return;
    }
    if (payerAccountNumbers?.length) {
      setPayersHydrated(true);
      return;
    }
    const payload = customerOrgAccountsSuccess;
    if (!payload) return;
    let data = payload?.data;
    if (
      data?.data &&
      typeof data.data === "object" &&
      !Array.isArray(data.data) &&
      data.account_number == null &&
      data.accountNumber == null
    ) {
      data = data.data;
    }
    if (!data || typeof data !== "object") return;
    const payeeSet = new Set(payeeAccountNumbers);
    const payers = new Set();
    const entries = Array.isArray(data) ? data : Object.values(data);
    entries.forEach((acc) => {
      if (!acc || typeof acc !== "object") return;
      const number = String(acc.account_number ?? acc.accountNumber ?? "").trim();
      if (!payeeSet.has(number)) return;
      const payer = String(acc.payer_account_id ?? acc.payerAccountId ?? "").trim();
      if (/^\d{12}$/.test(payer)) payers.add(payer);
      else if (/^\d{12}$/.test(number)) payers.add(number);
    });
    if (!payers.size) {
      payeeAccountNumbers.forEach((id) => payers.add(id));
    }
    // GET accountNumbers that are themselves payers must pre-check in the Payee
    // dropdown / Selected Accounts (payeeUiPayerNumbers), not only top scope.
    const uiPayers = [...payers].filter((id) => payeeSet.has(id));
    setPayerAccountNumbers([...payers]);
    setPayeeUiPayerNumbers(uiPayers);
    setPayersHydrated(true);
  }, [
    open,
    isEdit,
    payersHydrated,
    loadingRecord,
    payeeAccountNumbers,
    payerAccountNumbers,
    customerOrgAccountsSuccess,
  ]);

  // Handle create success
  useEffect(() => {
    if (addCustomerSuccess) {
      setFooterError("");
      setSnackbarMessage(addCustomerSuccess.message || "Customer created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
        clearFormState();
        onSuccess();
        dispatch(clearCustomerMessages());
        dispatch(resetCustomerOrgAccounts());
      }, 2000);
    }
  }, [addCustomerSuccess, onClose, onSuccess, dispatch]);

  // Handle update success
  useEffect(() => {
    if (updateCustomerSuccess) {
      setFooterError("");
      setSnackbarMessage(
        updateCustomerSuccess.message || "Customer updated successfully."
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
        clearFormState();
        onSuccess();
        dispatch(clearCustomerMessages());
        dispatch(resetCustomerOrgAccounts());
      }, 1500);
    }
  }, [updateCustomerSuccess, onClose, onSuccess, dispatch]);

  // API errors → footer (between Cancel and primary action)
  useEffect(() => {
    if (addCustomerError) {
      setFooterError(
        typeof addCustomerError === "string"
          ? addCustomerError
          : "Unable to create the customer."
      );
    }
  }, [addCustomerError]);

  useEffect(() => {
    if (updateCustomerError) {
      setFooterError(
        typeof updateCustomerError === "string"
          ? updateCustomerError
          : "Unable to update the customer."
      );
    }
  }, [updateCustomerError]);

  return (
    <>
      <Modal
        open={open}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key - only close via close button
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          onClose();
        }}
        aria-labelledby="customer-modal-title"
        aria-describedby="modal-for-customer-form"
      >
        <Paper
          sx={{
            ...auditHubPageSx,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 900 },
            maxWidth: 900,
            bgcolor: AH_TRACK,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            border: `1px solid ${AH_BORDER}`,
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 48px)",
            overflow: "hidden",
          }}
        >
          {/* Fixed header — Audit Hub / CSPM dialog chrome */}
          <Box
            sx={{
              display: "flex",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 56,
              px: 3,
              py: 1.25,
              backgroundColor: AH_ACCENT,
            }}
          >
            <Typography
              id="customer-modal-title"
              sx={{
                fontWeight: 700,
                fontSize: "1.0625rem",
                color: AH_WHITE,
                lineHeight: 1.3,
              }}
            >
              {isEdit ? "Edit Customer" : "Add Customer"}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-label="Close"
              disabled={isSaving}
              onClick={() => {
                clearFormState();
                onClose();
              }}
              sx={{
                p: 0,
                m: 0,
                border: "none",
                background: "transparent",
                color: AH_WHITE,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isSaving ? "default" : "pointer",
                opacity: isSaving ? 0.65 : 1,
              }}
            >
              ×
            </Box>
          </Box>

          {/* Scrollable body — Configuration Edit Account track grey */}
          <Box
            sx={{
              p: 3,
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
              backgroundColor: AH_TRACK,
            }}
          >
            {loadingRecord ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={32} sx={{ color: AH_ACCENT }} />
              </Box>
            ) : null}
            <form
              id="finops-add-customer-form"
              onSubmit={(e) => handleFormSubmit(e, "saved")}
              style={{ display: loadingRecord ? "none" : undefined }}
            >
              <Grid container spacing={2}>
                {/* Customer Information — Partner Central field chrome */}
                <Grid item xs={12}>
                  <Typography component="h3" sx={{ ...sectionSx, mt: 0, mb: 1.5 }}>
                    Customer Information
                  </Typography>
                  <Box sx={{ display: "grid", gap: 2 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-orgName" required>
                          Customer Name
                        </FieldLabel>
                        <TextField
                          id="finops-orgName"
                          name="orgName"
                          size="small"
                          fullWidth
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          placeholder="e.g., TechGlobal Corp"
                          required
                          sx={fieldSx}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="pc-customerId" required>
                          Customer ID
                        </FieldLabel>
                        <TextField
                          id="pc-customerId"
                          size="small"
                          fullWidth
                          value={commercialForm.customerId || ""}
                          onChange={(e) => {
                            const next = e.target.value;
                            setCommercialForm((prev) => ({ ...prev, customerId: next }));
                            if (commercialErrors.customerId) {
                              setCommercialErrors((prev) => {
                                const nextErrors = { ...prev };
                                delete nextErrors.customerId;
                                return nextErrors;
                              });
                            }
                          }}
                          placeholder="e.g., CUST-001"
                          required
                          error={Boolean(commercialErrors.customerId)}
                          helperText={
                            commercialErrors.customerId ||
                            "Unique customer code for Partner Central"
                          }
                          FormHelperTextProps={{
                            sx: commercialErrors.customerId
                              ? undefined
                              : { ...hintSx, mx: 0 },
                          }}
                          sx={fieldSx}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="finops-orgDescription" required>
                        Description
                      </FieldLabel>
                      <TextField
                        id="finops-orgDescription"
                        name="orgDescription"
                        size="small"
                        fullWidth
                        value={orgDescription}
                        onChange={(e) => setOrgDescription(e.target.value)}
                        placeholder="Brief description of the customer organization"
                        multiline
                        rows={3}
                        required
                        sx={fieldSx}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-orgPrimaryName" required>
                          Primary Contact Name
                        </FieldLabel>
                        <TextField
                          id="finops-orgPrimaryName"
                          name="orgPrimaryName"
                          size="small"
                          fullWidth
                          value={orgPrimaryName}
                          onChange={(e) => setOrgPrimaryName(e.target.value)}
                          placeholder="e.g., Jane Smith"
                          required
                          sx={fieldSx}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-orgPrimaryEmail" required>
                          Primary Contact Email
                        </FieldLabel>
                        <TextField
                          id="finops-orgPrimaryEmail"
                          name="orgPrimaryEmail"
                          size="small"
                          fullWidth
                          value={orgPrimaryEmail}
                          onChange={(e) => setOrgPrimaryEmail(e.target.value)}
                          placeholder="e.g., jane.smith@acmecorp.com"
                          required
                          sx={fieldSx}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-orgCountry" required>
                          Country
                        </FieldLabel>
                        <Autocomplete
                          id="finops-orgCountry"
                          fullWidth
                          size="small"
                          options={countryList}
                          getOptionLabel={(option) => option?.name || ""}
                          value={countryList.find((country) => country.name === orgCountry) || null}
                          onChange={(_event, newValue) => {
                            setOrgCountry(newValue?.name || "");
                            setOrgAddress("");
                            setAddressValidationStatus(null);
                            setAddressValidationMessage("");
                          }}
                          isOptionEqualToValue={(option, value) => option.name === value?.name}
                          slotProps={autocompleteSlotProps}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              required
                              placeholder="Select country..."
                              sx={fieldSx}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.code}>
                              {option.name}
                            </li>
                          )}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-phoneNumber" required>
                          Phone Number
                        </FieldLabel>
                        <TextField
                          id="finops-phoneNumber"
                          size="small"
                          fullWidth
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          onBlur={handlePhoneBlur}
                          placeholder="+1 (555) 123-4567"
                          required
                          error={Boolean(phonenumberError)}
                          helperText={phonenumberError || "Enter phone number with country code"}
                          FormHelperTextProps={{
                            sx: phonenumberError ? undefined : { ...hintSx, mx: 0 },
                          }}
                          sx={fieldSx}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="finops-orgAddress" required>
                        Address
                      </FieldLabel>
                      <TextField
                        id="finops-orgAddress"
                        size="small"
                        fullWidth
                        value={orgAddress}
                        onChange={(e) => {
                          setOrgAddress(e.target.value);
                          setAddressValidationStatus(null);
                          setAddressValidationMessage("");
                        }}
                        onBlur={validateAddress}
                        required
                        disabled={!orgCountry}
                        placeholder={
                          !orgCountry
                            ? "Please select a country first"
                            : "Enter complete address"
                        }
                        error={addressValidationStatus === "invalid"}
                        helperText={addressValidationMessage || ""}
                        FormHelperTextProps={{
                          sx: {
                            mx: 0,
                            mt: 0.5,
                            fontSize: 12,
                            color:
                              addressValidationStatus === "valid"
                                ? "#059669"
                                : addressValidationStatus === "invalid"
                                  ? "#dc2626"
                                  : AH_MUTED,
                          },
                        }}
                        InputProps={{
                          endAdornment:
                            addressValidationStatus === "valid" ? (
                              <i
                                className="mdi mdi-check-circle"
                                style={{ color: "#059669", marginRight: 4 }}
                              />
                            ) : null,
                        }}
                        sx={fieldSx}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-orgCity">City</FieldLabel>
                        <TextField
                          id="finops-orgCity"
                          size="small"
                          fullWidth
                          value={orgCity}
                          onChange={(e) => setOrgCity(e.target.value)}
                          placeholder="City"
                          sx={fieldSx}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-orgProvince">State/Province</FieldLabel>
                        <TextField
                          id="finops-orgProvince"
                          size="small"
                          fullWidth
                          value={orgProvince}
                          onChange={(e) => setOrgProvince(e.target.value)}
                          placeholder="State / Province"
                          sx={fieldSx}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="finops-orgZipcode">ZIP/Postal Code</FieldLabel>
                        <TextField
                          id="finops-orgZipcode"
                          size="small"
                          fullWidth
                          value={orgZipcode}
                          onChange={(e) => setOrgZipcode(e.target.value)}
                          placeholder="ZIP / Postal code"
                          sx={fieldSx}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* AWS Payer / Payee Accounts — same field as Partner Central */}
                <Grid item xs={12} sx={{ mt: 2.5 }}>
                  <Typography component="h3" sx={{ ...sectionSx, mb: 1.5 }}>
                    Customer Account Information
                  </Typography>
                  <Box sx={{ display: "grid", gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <PayerPayeeAccountsField
                        key={
                          isEdit
                            ? `edit-${customer?.orgcode || "customer"}-${payersHydrated ? "ready" : "loading"}`
                            : "create"
                        }
                        id="finops-payerAccountNumbers"
                        label="AWS Payer / Payee Accounts"
                        placeholder="Select accounts..."
                        hint="Link the AWS payer accounts associated with this customer"
                        required
                        payerOptions={payerAccounts}
                        linkedAccounts={payerAccounts}
                        payerAccountNumbers={payerAccountNumbers}
                        payeeAccountNumbers={payeeAccountNumbers}
                        payeeUiPayerNumbers={payeeUiPayerNumbers}
                        onAccountsMapChange={(_map, meta = {}) => {
                          if (Array.isArray(meta.payerAccountNumbers)) {
                            setPayerAccountNumbers(meta.payerAccountNumbers);
                          }
                          if (Array.isArray(meta.payeeAccountNumbers)) {
                            setPayeeAccountNumbers(meta.payeeAccountNumbers);
                          }
                          if (Array.isArray(meta.payeeUiPayerNumbers)) {
                            setPayeeUiPayerNumbers(meta.payeeUiPayerNumbers);
                          }
                          if (accountsError) setAccountsError("");
                        }}
                        fieldSx={fieldSx}
                      />
                      {accountsError ? (
                        <Typography sx={{ mt: 0.5, fontSize: 12, color: "#d32f2f" }}>
                          {accountsError}
                        </Typography>
                      ) : null}
                    </Box>

                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        border: `1px solid ${AH_BORDER}`,
                        borderRadius: "8px !important",
                        backgroundColor: AH_WHITE,
                        "&:before": { display: "none" },
                        overflow: "hidden",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: AH_MUTED }} />}
                        aria-controls="finops-regions-panel"
                        id="finops-regions-header"
                        sx={{
                          minHeight: 48,
                          px: 1.5,
                          "& .MuiAccordionSummary-content": {
                            my: 1,
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            pr: 1,
                          },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }}>
                            Primary Operating Regions
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                            AWS regions where this customer primarily operates
                          </Typography>
                        </Box>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 600, color: AH_MUTED, flexShrink: 0 }}
                        >
                          {regionsSummary(commercialForm.regions)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        id="finops-regions-panel"
                        sx={{
                          px: 1.5,
                          pt: 0,
                          pb: 1.5,
                          borderTop: `1px solid ${AH_BORDER}`,
                          backgroundColor: AH_WHITE,
                        }}
                      >
                        <Box
                          role="group"
                          aria-label="Primary Operating Regions"
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1, pt: 1.5 }}
                        >
                          <Chip
                            label="All Regions"
                            clickable
                            aria-pressed={!commercialForm.regions?.length}
                            onClick={() =>
                              setCommercialForm((prev) => ({ ...prev, regions: [] }))
                            }
                            sx={regionChipSx(!commercialForm.regions?.length)}
                          />
                          {AWS_REGION_CODES.map((code) => {
                            const selected = (commercialForm.regions || []).includes(code);
                            return (
                              <Chip
                                key={code}
                                label={code}
                                clickable
                                aria-pressed={selected}
                                onClick={() =>
                                  setCommercialForm((prev) => {
                                    const regions = prev.regions || [];
                                    return {
                                      ...prev,
                                      regions: selected
                                        ? regions.filter((region) => region !== code)
                                        : [...regions, code],
                                    };
                                  })
                                }
                                sx={regionChipSx(selected)}
                              />
                            );
                          })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Grid>
              </Grid>

              <FinOpsCustomerCommercialSection
                value={commercialForm}
                onChange={setCommercialForm}
                errors={commercialErrors}
              />

              <Typography
                sx={{
                  display: "block",
                  mt: 2.5,
                  fontSize: 12,
                  color: AH_MUTED,
                  lineHeight: 1.4,
                }}
              >
                Note: If you would like to give access to your customer, go to the customer&apos;s dashboard and add users in the configuration module.
              </Typography>
            </form>
          </Box>

          {/* Fixed footer — Cancel | centered error | Save/Add */}
          <Box sx={modalFooterSx}>
            <Button
              type="button"
              disabled={isSaving}
              onClick={() => {
                clearFormState();
                onClose();
              }}
              sx={{
                ...dialogButtonSx,
                gridArea: "cancel",
                backgroundColor: CLOSE_BG,
                color: CLOSE_TEXT,
                "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: CLOSE_BG, color: CLOSE_TEXT, opacity: 0.65 },
              }}
            >
              Cancel
            </Button>
            <Typography
              role={footerError ? "alert" : undefined}
              title={footerError || undefined}
              sx={footerMessageSx}
            >
              {footerError || ""}
            </Typography>
            <Button
              type="submit"
              form="finops-add-customer-form"
              disabled={loadingRecord || isOrgFormInvalid() || isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ ...primaryButtonSx, gridArea: "actions", minWidth: 140 }}
            >
              {isSaving
                ? isEdit
                  ? "Saving..."
                  : "Adding Customer..."
                : isEdit
                  ? "Save Changes"
                  : "Add Customer"}
            </Button>
          </Box>
        </Paper>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title" sx={{ fontWeight: 600, color: '#1e293b' }}>
          {isEdit ? "Confirm Customer Update" : "Confirm Customer Creation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" sx={{ color: '#1e293b', mb: 2 }}>
            {isEdit
              ? `Update ${getCustomerDisplayName(customer)} with the organization, account, and commercial details shown on this form.`
              : "Please ensure you have selected the correct account numbers for the selected customer. To undo this you will have to contact customer support."}
          </DialogContentText>
          <Alert severity="warning" sx={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
            {isEdit
              ? `You are about to update this customer with ${selectedAccountNumbers.length} account${selectedAccountNumbers.length !== 1 ? "s" : ""} and commercial settings.`
              : `You are about to create a customer with ${selectedAccountNumbers.length} account${selectedAccountNumbers.length !== 1 ? "s" : ""}. This action is irreversible.`}
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#f8fafc",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            sx={{
              ...dialogButtonSx,
              backgroundColor: CLOSE_BG,
              color: CLOSE_TEXT,
              "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            autoFocus
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ ...primaryButtonSx, minWidth: 140, ml: "auto" }}
          >
            {isSaving
              ? isEdit
                ? "Saving..."
                : "Adding Customer..."
              : isEdit
                ? "Confirm & Save"
                : "Confirm & Add Customer"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

CustomerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  customer: PropTypes.object,
};

export default CustomerModal;

